import { Setter } from "solid-js";
import { trimSpaces } from "./string";

/**
 * Processes input values by trimming spaces based on the input type
 * @param value - The input value to process
 * @param trimType - Type of trimming to apply: 'all' (remove all spaces) or 'edges' (trim start/end)
 * @returns Processed value with appropriate trimming applied
 */
export const processInput = (
  value: unknown,
  trimType: "all" | "edges" = "edges"
): string => {
  if (value === null || value === undefined) {
    return "";
  }

  const str = String(value);
  return trimType === "all" ? trimSpaces(str) : str.trim();
};

/**
 * Creates an input handler that processes the input value before setting it
 * @param setter - The state setter function
 * @param trimType - Type of trimming to apply: 'all' (remove all spaces) or 'edges' (trim start/end)
 * @returns A function that can be used as an event handler for input elements
 */
export const createInputHandler = (
  setter: Setter<string>,
  trimType: "all" | "edges" = "edges"
) => {
  return (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    setter(processInput(target.value, trimType));
  };
};

/**
 * Creates a form input handler that processes the input value before setting it
 * @param setter - The state setter function
 * @param field - The field name in the form state
 * @param trimType - Type of trimming to apply: 'all' (remove all spaces) or 'edges' (trim start/end)
 * @returns A function that can be used as an event handler for form inputs
 */
export const createFormInputHandler = <T extends object>(
  setter: Setter<T>,
  field: keyof T,
  trimType: "all" | "edges" = "edges"
) => {
  return (e: Event) => {
    const target = e.target as HTMLInputElement;
    setter(
      (prev) =>
        ({
          ...prev,
          [field]: processInput(target.value, trimType),
        }) as T
    );
  };
};
